import { useState, useCallback, useRef } from 'react';

/**
 * Shared hook for create/edit modal form state management.
 *
 * Accepts: { initialData, onSave, onClose }
 * Returns: { formData, setFormData, resetForm, loading, error, isDirty,
 *            handleSubmit, handleCancel, showConfirm, setShowConfirm }
 *
 * - isDirty is derived by deep-comparing formData to initialData
 * - handleSubmit wraps onSave with loading/error lifecycle and calls onClose on success
 * - handleCancel shows ConfirmCloseModal when dirty, calls onClose when pristine
 */
const useModalForm = ({ initialData, onSave, onClose }) => {
  // Deep clone initialData so mutations don't affect the original reference
  const initialRef = useRef(JSON.parse(JSON.stringify(initialData)));

  const [formData, setFormData] = useState(() =>
    JSON.parse(JSON.stringify(initialData))
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);

  // isDirty: derived from deep comparison between current formData and original initialData
  const isDirty =
    JSON.stringify(formData) !== JSON.stringify(initialRef.current);

  // Clear error when formData changes (spec: error cleared on re-edit)
  const setFormDataWithErrorClear = useCallback((valueOrUpdater) => {
    setError(null);
    setFormData(valueOrUpdater);
  }, []);

  // Reset formData to initialData and clear all transient state
  const resetForm = useCallback(() => {
    const freshInitial = JSON.parse(JSON.stringify(initialRef.current));
    setFormData(freshInitial);
    setError(null);
    setLoading(false);
    setShowConfirm(false);
  }, []);

  // Async submit: wraps onSave with loading/error lifecycle
  const handleSubmit = useCallback(
    async (e) => {
      if (e && e.preventDefault) {
        e.preventDefault();
      }
      setError(null);
      setLoading(true);
      try {
        await onSave(formData);
        onClose();
      } catch (err) {
        const message =
          err?.message || 'Error inesperado al guardar';
        setError(message);
      } finally {
        setLoading(false);
      }
    },
    [formData, onSave, onClose]
  );

  // Cancel: show confirm if dirty, else close directly
  const handleCancel = useCallback(() => {
    if (isDirty) {
      setShowConfirm(true);
    } else {
      onClose();
    }
  }, [isDirty, onClose]);

  return {
    formData,
    setFormData: setFormDataWithErrorClear,
    resetForm,
    loading,
    error,
    isDirty,
    handleSubmit,
    handleCancel,
    showConfirm,
    setShowConfirm,
  };
};

export default useModalForm;
